/** A SASE door onto a module the section shares with the cloud (plan SD-01,
 *  §7.1 rules 2–4, SASE-18), or onto one of SASE's own modules (SASE-14:
 *  Analytics, Incidents). The door is open when capability `sase` AND the
 *  module's own flag are on; inside it the module builds its links and
 *  breadcrumbs from `SASE_BASE`, so the visitor stays under `/sase/…`. The
 *  cloud's doors (Access → Private access, Compute → Agent gateways) keep their
 *  own flags and are unchanged. */
import type { ReactNode } from "react";
import { PageContainer } from "@ant-design/pro-components";
import { Card } from "antd";
import { Link } from "react-router-dom";

import EmptyState from "@/components/EmptyState";
import { useActingForStore } from "@/lib/actingFor";
import { useCapabilities, type SaseModules } from "@/lib/capabilities";
import {
  SASE_BASE,
  SASE_HOME,
  SectionBaseContext,
  trailItems,
  USERS_AND_DEVICES_TITLE,
} from "@/lib/sectionBase";

import { SASE_NOT_AVAILABLE } from "./saseText";

export type SaseDoorModule =
  | "privateAccess"
  | "agentGateways"
  | "analytics"
  | "incidents"
  | "aiSecurity"
  | "dataProtection"
  | "experience"
  | "usersAndDevices";

const MODULES: Record<
  SaseDoorModule,
  { title: string; off: string; next?: { label: string; to: string } }
> = {
  privateAccess: {
    title: "Private access",
    off: "Private access is not switched on for this cloud. Your provider turns it on.",
  },
  agentGateways: {
    title: "Agent gateways",
    off: "Agent gateways are not switched on for this cloud. Your provider turns them on.",
  },
  // SASE's own modules (SASE-14): the same gate, `sase` AND the module flag.
  analytics: {
    title: "Analytics",
    off: "Analytics is not switched on for this cloud. Your provider turns it on.",
  },
  incidents: {
    title: "Incidents",
    off: "Incidents are not switched on for this cloud. Your provider turns them on.",
  },
  // SASE-13: AI security's own pages (not a shared module; the same gate).
  aiSecurity: {
    title: "AI security",
    off: "AI security is not switched on for this cloud. Your provider turns it on.",
  },
  // SASE-24: Data protection's own pages (Exact data first; the same gate).
  dataProtection: {
    title: "Data protection",
    off: "Data protection is not switched on for this cloud. Your provider turns it on.",
  },
  // SASE-29 (B25): Experience's own page (the same gate).
  experience: {
    title: "Experience",
    off: "Experience monitoring is not switched on for this cloud. Your provider turns it on.",
  },
  // C3: Users and devices are Private access's people and devices, so the door
  // is Private access's flag; it says what the page is and what still works.
  usersAndDevices: {
    title: USERS_AND_DEVICES_TITLE,
    off: "Users and devices come from Private access. Private access is not switched on for this cloud. Your provider turns it on.",
    next: { label: "See what SASE protects without it", to: `${SASE_HOME}/get-started` },
  },
};

/** SASE-35 (P35c): the shared modules whose pages are not SASE routes, so a
 *  partner managing a customer cannot use them for it (P35c-F3). */
const NOT_FOR_CUSTOMERS: ReadonlySet<SaseDoorModule> = new Set(["privateAccess", "agentGateways"]);

/** Whether the module's own flag is on (plan §7.1 rule 4). */
function moduleFlag(module: SaseDoorModule, modules: SaseModules | undefined): boolean {
  if (!modules) return false;
  switch (module) {
    case "privateAccess":
    case "usersAndDevices":
      return Boolean(modules.private_access);
    case "agentGateways":
      return Boolean(modules.agent_gateways);
    case "analytics":
      return Boolean(modules.analytics);
    case "incidents":
      return Boolean(modules.incidents);
    case "aiSecurity":
      return Boolean(modules.ai_security);
    case "dataProtection":
      return Boolean(modules.data_protection);
    case "experience":
      return Boolean(modules.experience);
  }
}

export default function SaseDoor({
  module,
  children,
}: {
  module: SaseDoorModule;
  children: ReactNode;
}) {
  const capabilities = useCapabilities();
  const acting = useActingForStore((st) => st.acting);
  const sase = capabilities.sase;
  const enabled = Boolean(sase?.enabled);
  const moduleOn = moduleFlag(module, sase?.modules);
  if (!enabled || !moduleOn) {
    const { title, off, next } = MODULES[module];
    // Agent gateways' trail is the layout's breadcrumb (components/Breadcrumbs);
    // Private access and AI security carry theirs in the page header.
    const breadcrumb =
      module === "agentGateways"
        ? undefined
        : { items: trailItems(SASE_BASE.privateAccessTrail, title) };
    return (
      <PageContainer title={title} breadcrumb={breadcrumb}>
        <Card>
          <EmptyState description={enabled ? off : SASE_NOT_AVAILABLE} />
          {enabled && next ? (
            <div style={{ textAlign: "center" }}>
              <Link to={next.to} data-testid="sase-door-next">
                {next.label}
              </Link>
            </div>
          ) : null}
        </Card>
      </PageContainer>
    );
  }
  if (acting && NOT_FOR_CUSTOMERS.has(module)) {
    // SASE-35 (P35c, P35c-F3): these modules' pages read their own APIs, which a
    // partner cannot use for a customer yet; they would show the partner's own data.
    const { title } = MODULES[module];
    return (
      <PageContainer title={title}>
        <Card>
          <EmptyState
            description={`${title} cannot be managed for a customer yet: this page would show your own organisation, not ${acting.name}'s.`}
          />
        </Card>
      </PageContainer>
    );
  }
  return <SectionBaseContext.Provider value={SASE_BASE}>{children}</SectionBaseContext.Provider>;
}
